import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, X } from 'lucide-react'
import { fetchCustomer, fetchCustomerAgents, fetchCustomerSummary } from '@/api/customers'
import { ColumnPicker } from '@/components/ColumnPicker'
import { AgentsTable } from '@/components/tables/AgentsTable'
import { TrafficLightDot } from '@/components/PostureIndicator'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DEVICE_COLUMNS } from '@/config/columns'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { combinedDeviceStatus, TRAFFIC } from '@/lib/trafficLight'

const POSTURE_FILTERS = new Set(['red', 'amber', 'green', 'grey'])
const STORAGE_KEY = 'customer-devices-columns-v1'

export function CustomerDetail() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const postureFilter = POSTURE_FILTERS.has(searchParams.get('posture'))
    ? searchParams.get('posture')
    : null

  const deviceColumns = useMemo(
    () => DEVICE_COLUMNS.filter((c) => !c.hideForCustomer),
    [],
  )
  const {
    visibleIds,
    toggleColumn,
    reorderVisible,
    showAll,
    resetDefaults,
  } = useColumnVisibility(STORAGE_KEY, deviceColumns)

  const [customer, setCustomer] = useState(null)
  const [summary, setSummary] = useState(null)
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sortBy, setSortBy] = useState('hostname')
  const [sortDir, setSortDir] = useState('asc')

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchCustomer(id),
      fetchCustomerSummary(id),
      fetchCustomerAgents(id, { page: 1, pageSize: 500, sortBy, sortDir }),
    ])
      .then(([c, s, a]) => {
        setCustomer(c)
        setSummary(s)
        setAgents(a.items)
      })
      .catch((err) => {
        const detail = err.response?.data?.detail
        setError(
          typeof detail === 'string' ? detail : 'Could not load customer details.',
        )
      })
      .finally(() => setLoading(false))
  }, [id, sortBy, sortDir])

  const filteredAgents = useMemo(() => {
    if (!postureFilter) return agents
    return agents.filter(
      (agent) =>
        combinedDeviceStatus(agent.security_status, agent.hardware_status) === postureFilter,
    )
  }, [agents, postureFilter])

  function handleSort(column) {
    if (!visibleIds.includes(column)) return
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDir('asc')
    }
  }

  function clearPostureFilter() {
    const next = new URLSearchParams(searchParams)
    next.delete('posture')
    setSearchParams(next)
  }

  if (loading) return <Skeleton className="h-64 w-full" />
  if (error || !customer) {
    return <p className="text-destructive">{error}</p>
  }

  const filterTraffic = postureFilter ? TRAFFIC[postureFilter] : null

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/customers">
          <ArrowLeft className="h-4 w-4" />
          Back to customers
        </Link>
      </Button>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Devices" value={summary?.agent_count} />
        <StatCard label="Online" value={summary?.online_count} />
        <StatCard label="Offline" value={summary?.offline_count} />
        <StatCard label="Open alerts" value={summary?.active_alerts} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{customer.name}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {customer.domain && <p>Domain: {customer.domain}</p>}
          {customer.phone && <p>Phone: {customer.phone}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle>Devices</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {postureFilter && filterTraffic && (
              <>
                <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <TrafficLightDot status={postureFilter} className="h-2.5 w-2.5" />
                  Showing {filteredAgents.length} of {agents.length} — {filterTraffic.label}
                </span>
                <Badge variant={filterTraffic.badge}>{filterTraffic.label}</Badge>
                <Button variant="ghost" size="sm" onClick={clearPostureFilter}>
                  <X className="h-4 w-4" />
                  Clear filter
                </Button>
              </>
            )}
            <ColumnPicker
              columns={deviceColumns}
              visibleIds={visibleIds}
              onToggle={toggleColumn}
              onReorder={reorderVisible}
              onShowAll={showAll}
              onResetDefaults={resetDefaults}
              buttonLabel="Edit columns"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredAgents.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {postureFilter
                ? 'No devices match this health filter.'
                : 'No devices for this customer.'}
            </p>
          ) : (
            <AgentsTable
              agents={filteredAgents}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
              hideCustomer
              visibleColumnIds={visibleIds}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatCard({ label, value }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value ?? '—'}</p>
      </CardContent>
    </Card>
  )
}
